import { useCallback, useEffect, useMemo, useState } from 'react'
import {
 getOfflineBootstrapStatus,
 runManualOfflineSyncNow,
 subscribeOfflineBootstrap,
 formatBootstrapModuleLabel,
 flushOfflineBootstrapPendingCounts,
 attentionTotalFromPendingActions,
 type OfflineBootstrapStatus,
 type ManualOfflineSyncOutcome,
} from 'services/offlineBootstrap'
import { notifyManualSyncOutcome } from 'services/manualSyncFeedback'

export function useOfflineBootstrapStatus() {
 const [status, setStatus] = useState<OfflineBootstrapStatus | null>(null)
 const [syncing, setSyncing] = useState(false)

 useEffect(() => {
 let mounted = true
 void getOfflineBootstrapStatus().then((next) => {
 if (mounted) setStatus(next)
 }).catch(() => {})
 const unsubscribe = subscribeOfflineBootstrap((next) => {
 if (mounted) setStatus({ ...next, modules: { ...next.modules } })
 })
 return () => {
 mounted = false
 unsubscribe()
 }
 }, [])

 const refreshOfflineBootstrapStatus = useCallback(async () => {
 const flushed = await flushOfflineBootstrapPendingCounts()
 if (flushed) setStatus({ ...flushed, modules: { ...flushed.modules } })
 else {
 const next = await getOfflineBootstrapStatus()
 if (next) setStatus({ ...next, modules: { ...next.modules } })
 }
 }, [])

 const syncNow = useCallback(async (): Promise<ManualOfflineSyncOutcome> => {
 setSyncing(true)
 try {
 const outcome = await runManualOfflineSyncNow()
 setStatus({ ...outcome.bootstrap, modules: { ...outcome.bootstrap.modules } })
 notifyManualSyncOutcome(outcome)
 return outcome
 } finally {
 setSyncing(false)
 }
 }, [])

 const summary = useMemo(() => {
 const modules = Object.entries(status?.modules ?? {})
 const visible = modules.filter(([, moduleStatus]) => moduleStatus.status !== 'skipped')
 const completed = visible.filter(([, moduleStatus]) => moduleStatus.status === 'success').length
 const failed = visible.filter(([, moduleStatus]) => moduleStatus.status === 'failed').length
 const running = visible.find(([, moduleStatus]) => moduleStatus.status === 'running') ?? null
 const pa = status?.pending_actions
 const pendingMutations = pa?.pending_mutations ?? 0
 const failedMutations = pa?.failed_mutations ?? 0
 const pendingPos = pa?.pending_pos_orders ?? 0
 const pendingTableOps = pa?.pending_table_ops ?? 0
 const failedOrders = pa?.failed_orders ?? 0
 const failedTableOps = pa?.failed_table_ops ?? 0
 const mutationConflicts = pa?.mutation_conflicts ?? 0
 const posConflicts = pa?.pos_order_conflicts ?? 0
 const tableOpConflicts = pa?.table_op_conflicts ?? 0
 const conflictCount = mutationConflicts + posConflicts + tableOpConflicts
 const pendingForSync = pendingMutations + pendingPos + pendingTableOps
 const attentionCount = pa ? attentionTotalFromPendingActions(pa) : 0

 const failedModuleDetails = visible
 .filter(([, moduleStatus]) => moduleStatus.status === 'failed')
 .map(([moduleId, moduleStatus]) => ({
 id: moduleId,
 label: formatBootstrapModuleLabel(moduleId),
 error: moduleStatus.error ?? '',
 }))
 return {
 totalModules: visible.length,
 completedModules: completed,
 failedModules: failed,
 failedModuleDetails,
 runningModuleId: running?.[0] ?? null,
 runningModule: running?.[1] ?? null,
 /** قيد المزامنة (بدون تعارض) */
 pendingForSync,
 pendingMutations,
 pendingPosOrders: pendingPos,
 /** فشل POS + طاولات (للقوائم الحالية) */
 failedOrders: failedOrders + failedTableOps,
 failedPosOrders: failedOrders,
 failedTableOps,
 /** فشل طابور العمليات الأخرى */
 failedMutations,
 pendingTableOps,
 /** تعارض يحتاج مراجعة */
 conflictCount,
 tableOpConflicts,
 mutationConflicts,
 posConflicts,
 /** مجموع انتباه الشارة — بدون double-count */
 attentionCount,
 staleModules: visible.filter(([, moduleStatus]) => Boolean(moduleStatus.stale)).length,
 }
 }, [status])

 return {
 status,
 bootstrapRunning: status?.status === 'running',
 syncing: syncing || status?.status === 'running',
 syncNow,
 refreshOfflineBootstrapStatus,
 summary,
 }
}
